import { useCallback, useEffect, useMemo, useState } from "react";
import { getStoredAdminProducts, useAdminProducts } from "@/hooks/useAdminStore";

const MONITOR_INTERVAL_MS = 24 * 60 * 60 * 1000;
const HISTORY_LIMIT = 8;
const REPORT_STORAGE_KEY = "afiliabr_admin_link_monitor_report";
const REPORT_EVENT = "afiliabr-admin-link-monitor-report-updated";

const PROBLEM_STATUSES = new Set<LinkMonitorStatus>(["missing", "invalid", "broken", "timeout", "error"]);
const HEALTHY_STATUSES = new Set<LinkMonitorStatus>(["ok", "redirected"]);

let activeRun: Promise<void> | null = null;

export type LinkMonitorStatus =
  | "pending"
  | "ok"
  | "redirected"
  | "missing"
  | "invalid"
  | "broken"
  | "timeout"
  | "error";

export type LinkMonitorItemType = "product" | "deal";

export interface LinkMonitorHistoryEntry {
  status: LinkMonitorStatus;
  checkedAt: number;
  httpStatus: number | null;
  finalUrl: string | null;
  redirected: boolean;
  latencyMs: number | null;
  detail: string;
  checkMethod: "HEAD" | "GET" | "LOCAL" | null;
}

export interface LinkMonitorRecord {
  key: string;
  itemType: LinkMonitorItemType;
  itemId: number;
  itemName: string;
  marketplace: string;
  link: string;
  editPath: string;
  status: LinkMonitorStatus;
  checkedAt: number | null;
  lastHealthyAt: number | null;
  httpStatus: number | null;
  finalUrl: string | null;
  redirected: boolean;
  latencyMs: number | null;
  detail: string;
  checkMethod: "HEAD" | "GET" | "LOCAL" | null;
  history: LinkMonitorHistoryEntry[];
}

export interface LinkMonitorReport {
  lastRunAt: number | null;
  nextRunAt: number | null;
  records: LinkMonitorRecord[];
}

interface ApiCheckResponse {
  status: Extract<LinkMonitorStatus, "ok" | "redirected" | "broken" | "timeout" | "error">;
  httpStatus: number | null;
  finalUrl: string | null;
  redirected: boolean;
  latencyMs: number | null;
  detail: string;
  checkMethod: "HEAD" | "GET" | null;
}

interface MonitoredProductItem {
  id: number;
  name: string;
  marketplace: string;
  affiliateLink: string;
  showInDailyDeals?: boolean;
}

interface MonitoredItem {
  key: string;
  itemType: LinkMonitorItemType;
  itemId: number;
  itemName: string;
  marketplace: string;
  link: string;
  editPath: string;
}

function createEmptyReport(): LinkMonitorReport {
  return {
    lastRunAt: null,
    nextRunAt: null,
    records: [],
  };
}

function isBrowser() {
  return typeof window !== "undefined";
}

function saveReport(report: LinkMonitorReport) {
  if (!isBrowser()) return;
  localStorage.setItem(REPORT_STORAGE_KEY, JSON.stringify(report));
  window.dispatchEvent(new CustomEvent(REPORT_EVENT, { detail: report }));
}

export function getStoredLinkMonitorReport(): LinkMonitorReport {
  if (!isBrowser()) return createEmptyReport();

  try {
    const raw = localStorage.getItem(REPORT_STORAGE_KEY);
    if (!raw) return createEmptyReport();
    const parsed = JSON.parse(raw) as LinkMonitorReport;
    return {
      lastRunAt: parsed.lastRunAt ?? null,
      nextRunAt: parsed.nextRunAt ?? null,
      records: Array.isArray(parsed.records) ? parsed.records : [],
    };
  } catch {
    return createEmptyReport();
  }
}

function getReturnPath() {
  return encodeURIComponent("/admin/relatorios?tab=broken-links");
}

function createItemKey(itemType: LinkMonitorItemType, itemId: number) {
  return `${itemType}:${itemId}`;
}

function createBaseRecord(item: MonitoredItem): LinkMonitorRecord {
  return {
    ...item,
    status: "pending",
    checkedAt: null,
    lastHealthyAt: null,
    httpStatus: null,
    finalUrl: null,
    redirected: false,
    latencyMs: null,
    detail: "Aguardando a primeira verificação automática.",
    checkMethod: null,
    history: [],
  };
}

function reconcileReport(report: LinkMonitorReport, items: MonitoredItem[]): LinkMonitorReport {
  const currentByKey = new Map(report.records.map((record) => [record.key, record]));

  const records = items.map((item) => {
    const existing = currentByKey.get(item.key);
    if (!existing) return createBaseRecord(item);

    return {
      ...existing,
      itemName: item.itemName,
      marketplace: item.marketplace,
      link: item.link,
      editPath: item.editPath,
      itemType: item.itemType,
      itemId: item.itemId,
      key: item.key,
    };
  });

  return {
    ...report,
    records,
  };
}

function isValidHttpUrl(value: string) {
  if (!value || !value.trim() || value === "#") return false;

  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function createLocalResult(item: MonitoredItem): Omit<LinkMonitorHistoryEntry, "checkedAt"> {
  if (!item.link || !item.link.trim() || item.link === "#") {
    return {
      status: "missing",
      httpStatus: null,
      finalUrl: null,
      redirected: false,
      latencyMs: null,
      detail: "Nenhum link de afiliado foi cadastrado para este item.",
      checkMethod: "LOCAL",
    };
  }

  if (!isValidHttpUrl(item.link)) {
    return {
      status: "invalid",
      httpStatus: null,
      finalUrl: null,
      redirected: false,
      latencyMs: null,
      detail: "O link cadastrado não é uma URL HTTP/HTTPS válida.",
      checkMethod: "LOCAL",
    };
  }

  return {
    status: "pending",
    httpStatus: null,
    finalUrl: null,
    redirected: false,
    latencyMs: null,
    detail: "",
    checkMethod: null,
  };
}

async function requestLinkCheck(url: string): Promise<ApiCheckResponse> {
  const response = await fetch("/api/link-monitor/check", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ url }),
  });

  if (!response.ok) {
    return {
      status: "error",
      httpStatus: response.status,
      finalUrl: null,
      redirected: false,
      latencyMs: null,
      detail: "A API local de monitoramento não conseguiu responder à verificação.",
      checkMethod: null,
    };
  }

  return (await response.json()) as ApiCheckResponse;
}

function applyResult(record: LinkMonitorRecord, result: Omit<LinkMonitorHistoryEntry, "checkedAt">, checkedAt: number): LinkMonitorRecord {
  const nextHistoryEntry: LinkMonitorHistoryEntry = {
    ...result,
    checkedAt,
  };

  return {
    ...record,
    status: result.status,
    checkedAt,
    lastHealthyAt: HEALTHY_STATUSES.has(result.status) ? checkedAt : record.lastHealthyAt,
    httpStatus: result.httpStatus,
    finalUrl: result.finalUrl,
    redirected: result.redirected,
    latencyMs: result.latencyMs,
    detail: result.detail,
    checkMethod: result.checkMethod,
    history: [nextHistoryEntry, ...record.history].slice(0, HISTORY_LIMIT),
  };
}

function sortRecords(records: LinkMonitorRecord[]) {
  return [...records].sort((a, b) => {
    const aProblem = PROBLEM_STATUSES.has(a.status) ? 0 : 1;
    const bProblem = PROBLEM_STATUSES.has(b.status) ? 0 : 1;
    if (aProblem !== bProblem) return aProblem - bProblem;
    return (b.checkedAt ?? 0) - (a.checkedAt ?? 0);
  });
}

function buildItems(products: MonitoredProductItem[]): MonitoredItem[] {
  return [
    ...products.map((product) => ({
      key: createItemKey("product", product.id),
      itemType: "product" as const,
      itemId: product.id,
      itemName: product.name,
      marketplace: product.marketplace,
      link: product.affiliateLink,
      editPath: `/admin/produtos?edit=${product.id}&returnTo=${getReturnPath()}`,
    })),
    ...products
      .filter((product) => product.showInDailyDeals)
      .map((product) => ({
        key: createItemKey("deal", product.id),
        itemType: "deal" as const,
        itemId: product.id,
        itemName: product.name,
        marketplace: product.marketplace,
        link: product.affiliateLink,
        editPath: `/admin/produtos?edit=${product.id}&returnTo=${getReturnPath()}`,
      })),
  ];
}

function getCurrentItems() {
  return buildItems(getStoredAdminProducts());
}

function hasReportChanged(left: LinkMonitorReport, right: LinkMonitorReport) {
  return JSON.stringify(left) !== JSON.stringify(right);
}

export function useAdminLinkMonitor(options?: { autoRun?: boolean }) {
  const autoRun = options?.autoRun ?? false;
  const { products } = useAdminProducts();
  const items = useMemo(() => buildItems(products), [products]);
  const [report, setReport] = useState<LinkMonitorReport>(() => reconcileReport(getStoredLinkMonitorReport(), getCurrentItems()));
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  useEffect(() => {
    const nextReport = reconcileReport(getStoredLinkMonitorReport(), items);
    setReport(nextReport);
    if (hasReportChanged(nextReport, getStoredLinkMonitorReport())) {
      saveReport(nextReport);
    }
  }, [items]);

  useEffect(() => {
    if (!isBrowser()) return;

    const handleCustomUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<LinkMonitorReport>;
      setReport(customEvent.detail);
    };

    const handleStorageUpdate = (event: StorageEvent) => {
      if (event.key === REPORT_STORAGE_KEY) {
        setReport(reconcileReport(getStoredLinkMonitorReport(), getCurrentItems()));
      }
    };

    window.addEventListener(REPORT_EVENT, handleCustomUpdate as EventListener);
    window.addEventListener("storage", handleStorageUpdate);

    return () => {
      window.removeEventListener(REPORT_EVENT, handleCustomUpdate as EventListener);
      window.removeEventListener("storage", handleStorageUpdate);
    };
  }, []);

  const runMonitor = useCallback(
    async (targetKeys?: string[]) => {
      if (activeRun) {
        await activeRun;
        return;
      }

      const runPromise = (async () => {
        setIsRunning(true);

        const latest = reconcileReport(getStoredLinkMonitorReport(), getCurrentItems());
        let nextReport = latest;
        const recordsToCheck = sortRecords(latest.records).filter((record) => !targetKeys || targetKeys.includes(record.key));

        setProgress({ current: 0, total: recordsToCheck.length });

        for (let index = 0; index < recordsToCheck.length; index += 1) {
          const record = recordsToCheck[index];
          const checkedAt = Date.now();
          const localResult = createLocalResult(record);

          let result: Omit<LinkMonitorHistoryEntry, "checkedAt"> = localResult;

          if (localResult.status === "pending") {
            result = await requestLinkCheck(record.link);
          }

          nextReport = {
            ...nextReport,
            records: nextReport.records.map((entry) => entry.key === record.key ? applyResult(entry, result, checkedAt) : entry),
          };

          saveReport(nextReport);
          setReport(nextReport);
          setProgress({ current: index + 1, total: recordsToCheck.length });
        }

        const finishedAt = Date.now();
        nextReport = {
          ...nextReport,
          lastRunAt: finishedAt,
          nextRunAt: finishedAt + MONITOR_INTERVAL_MS,
        };

        saveReport(nextReport);
        setReport(nextReport);
      })().finally(() => {
        activeRun = null;
        setIsRunning(false);
        setProgress({ current: 0, total: 0 });
      });

      activeRun = runPromise;
      await runPromise;
    },
    []
  );

  const runCheckForItem = useCallback(
    async (itemType: LinkMonitorItemType, itemId: number) => {
      await runMonitor([createItemKey(itemType, itemId)]);
    },
    [runMonitor]
  );

  useEffect(() => {
    if (!autoRun || !isBrowser()) return;

    const latest = reconcileReport(getStoredLinkMonitorReport(), getCurrentItems());
    const now = Date.now();

    if (!latest.lastRunAt || !latest.nextRunAt || now >= latest.nextRunAt) {
      void runMonitor();
    }

    const nextDelay = Math.max((latest.nextRunAt ?? now + MONITOR_INTERVAL_MS) - now, 60 * 1000);
    const timer = window.setTimeout(() => {
      void runMonitor();
    }, nextDelay);

    return () => {
      window.clearTimeout(timer);
    };
  }, [autoRun, report.nextRunAt, runMonitor]);

  const records = useMemo(() => sortRecords(report.records), [report.records]);
  const brokenRecords = useMemo(() => records.filter((record) => PROBLEM_STATUSES.has(record.status)), [records]);
  const healthyCount = useMemo(() => records.filter((record) => HEALTHY_STATUSES.has(record.status)).length, [records]);
  const redirectedCount = useMemo(() => records.filter((record) => record.status === "redirected").length, [records]);
  const brokenCount = brokenRecords.length;

  return {
    report: {
      ...report,
      records,
    },
    records,
    brokenRecords,
    brokenCount,
    healthyCount,
    redirectedCount,
    isRunning,
    progress,
    monitorIntervalMs: MONITOR_INTERVAL_MS,
    runMonitor,
    runCheckForItem,
  };
}
